import { ColumnMeta, ResultColumn } from '../../Types/DataServiceType';

/**
 * A single row addressed by its primary key, rather than by a snapshot of its
 * values.
 *
 * <p>Carrying the key in the URL instead of the row itself is deliberate. A
 * record opened in a new tab has to survive a refresh and a bookmark, and it
 * has to be re-checked against the caller's current allowlist at the moment it
 * is opened. Passing a frozen copy of the row would do neither.</p>
 */
export interface RecordRef {
  connectionId: string;
  table: string;
  primaryKey: Record<string, string>;
}

export type RecordKind = 'candidate' | 'job-template' | 'generic';

export const RECORD_PATH = '/data-service/record';

/**
 * Two tables that need a real detail screen instead of a field dump.
 *
 * <p>Comparison is done on a bare, underscore-free, lower-cased name because
 * the same table is spelled differently depending on where you look: the JPA
 * entity says `JobTemplate` while the baseline migration created
 * `job_template`. Matching the raw string would silently drop job templates
 * into the generic view.</p>
 */
const DETAIL_TABLES: Record<string, RecordKind> = {
  candidates: 'candidate',
  candidate: 'candidate',
  jobtemplate: 'job-template',
};

/** Strips any `schema.` prefix and backtick quoting, preserving real casing. */
export const bareTableName = (table: string): string => {
  const segments = table.trim().split('.');
  return (segments[segments.length - 1] ?? '').replace(/`/g, '');
};

export const normalizeTable = (table: string): string => bareTableName(table).toLowerCase();

export const recordKind = (table: string): RecordKind => {
  const bare = normalizeTable(table).replace(/_/g, '');
  return DETAIL_TABLES[bare] ?? 'generic';
};

/** Turns a positional result row into a label-keyed object. */
export const toRecord = (columns: ResultColumn[], row: unknown[]): Record<string, unknown> => {
  const record: Record<string, unknown> = {};
  columns.forEach((column, index) => {
    record[column.label] = row[index];
  });
  return record;
};

/**
 * Collects the primary-key values a row can be looked up by.
 *
 * <p>Returns null when the result set exposes no primary key, because a record
 * page cannot be built for a projection the database did not identify as a
 * real row.</p>
 */
export const extractPrimaryKey = (
  columns: ResultColumn[],
  row: unknown[]
): Record<string, string> | null => {
  const primaryKey: Record<string, string> = {};
  columns.forEach((column, index) => {
    if (!column.primaryKey) return;
    const value = row[index];
    if (value === null || value === undefined) return;
    primaryKey[column.label] = String(value);
  });
  return Object.keys(primaryKey).length > 0 ? primaryKey : null;
};

export const buildRecordPath = (ref: RecordRef): string => {
  const params = new URLSearchParams();
  params.set('c', ref.connectionId);
  params.set('t', ref.table);
  Object.entries(ref.primaryKey).forEach(([column, value]) => {
    params.set(`pk.${column}`, value);
  });
  return `${RECORD_PATH}?${params.toString()}`;
};

export const parseRecordRef = (search: string): RecordRef | null => {
  const params = new URLSearchParams(search);
  const connectionId = params.get('c');
  const table = params.get('t');
  if (!connectionId || !table) return null;

  const primaryKey: Record<string, string> = {};
  params.forEach((value, key) => {
    if (key.startsWith('pk.') && key.length > 3) primaryKey[key.slice(3)] = value;
  });
  if (Object.keys(primaryKey).length === 0) return null;

  return { connectionId, table, primaryKey };
};

/**
 * Best-effort table name for a hand-written statement.
 *
 * <p>The console is a raw-SQL surface with no declared table, so a result grid
 * there has nothing to drill into by default. This looks for a table in the
 * FROM/JOIN clauses and only answers when there is exactly one. Anything
 * ambiguous returns null, so the row simply is not clickable.</p>
 *
 * <p>The name is returned with its original casing, not normalised, because it
 * is fed straight back to the database as a table name and table names are
 * case-sensitive on a case-sensitive filesystem. De-duplication is
 * case-insensitive so `FROM Candidates` and `from candidates` still count as
 * one table.</p>
 *
 * <p>This is a convenience hint, never an authorization decision: the record
 * page re-reads the row through the guarded query endpoint, which checks the
 * caller's allowlist for the table name it is given. A wrong guess here fails
 * closed at the server.</p>
 */
export const inferSingleTable = (sql: string): string | null => {
  const pattern = /\b(?:from|join)\s+`?([A-Za-z0-9_$.]+)`?/gi;
  const found = new Map<string, string>();

  let match = pattern.exec(sql);
  while (match !== null) {
    const token = match[1];
    if (!/^(select|values|dual)$/i.test(token)) {
      const bare = bareTableName(token);
      if (bare) found.set(bare.toLowerCase(), bare);
    }
    match = pattern.exec(sql);
  }

  return found.size === 1 ? Array.from(found.values())[0] : null;
};

/**
 * Resolves which of a result set's columns identify a single row.
 *
 * <p>This has to come from the table's own metadata rather than the result
 * columns. A query result is built straight from JDBC and carries no key
 * information, so {@link ResultColumn#primaryKey} is never set on it; the
 * authoritative list is the one the schema endpoint reports for the
 * connection's table.</p>
 *
 * <p>Intersecting with the result's own labels is what makes a partial select
 * behave: if the builder was told to show three columns and none of them is
 * the key, the row is not addressable and must not be clickable.</p>
 */
export const primaryKeyLabelsFor = (
  meta: ColumnMeta[],
  resultColumns: ResultColumn[]
): string[] => {
  const byLowerName = new Map(
    meta.map((column) => [column.columnName.toLowerCase(), column] as const)
  );

  return resultColumns
    .filter((column) => byLowerName.get(column.label.toLowerCase())?.primaryKey)
    .map((column) => column.label);
};
